import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

import Dropdown from 'shared/components/ui/DropDown/DropDown';

import ArrowDown from 'assets/arrow-down.svg?react';

export interface selectOption {
  value: string | number;
  label: string;
}

export interface IBadgeSelect {
  value: string;
  setValue: (selectOption: string) => void;
  icon?: React.ReactNode;
  options: selectOption[];
  className?: string;
}

export const BadgeSelect = ({ value, setValue, icon, options = [], className }: IBadgeSelect) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleOpen = () => setOpen(!open);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef} className={clsx('relative cursor-pointer', className)}>
      <div
        className="flex min-h-[2.5rem] items-center justify-between gap-1 rounded-[1.5rem] border-2 border-border bg-indigo p-2 text-md text-light-grape sm:gap-4 sm:px-4 sm:text-[20px]"
        onClick={toggleOpen}
        data-testid="selected-option"
      >
        {icon && <span className="h-5 w-5">{icon}</span>}
        {t(options.find((option) => option.value === value)?.label || '')}
        <ArrowDown
          className={`w-3 transition-transform duration-300 ${open ? 'rotate-180 transform' : ''}`}
        />
      </div>
      <Dropdown
        open={open}
        list={[...options]}
        onClick={(option) => {
          toggleOpen();
          setValue(String(option.value));
        }}
        position={{ top: 60, left: 0 }}
      />
    </div>
  );
};
